
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Save } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const SettingsTab = () => {
  const [generalSettings, setGeneralSettings] = useState({
    emailNotifications: true,
    marketingEmails: false,
    darkMode: false,
  });

  const [privacySettings, setPrivacySettings] = useState({
    publicProfile: false,
    shareData: false,
  });

  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  const handleToggleGeneralSetting = (setting: keyof typeof generalSettings) => {
    setGeneralSettings({
      ...generalSettings,
      [setting]: !generalSettings[setting]
    });
  };

  const handleTogglePrivacySetting = (setting: keyof typeof privacySettings) => {
    setPrivacySettings({
      ...privacySettings,
      [setting]: !privacySettings[setting]
    });
  };

  const saveGeneralSettings = async () => {
    setSavingGeneral(true);
    try {
      // Logique pour sauvegarder les paramètres généraux
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulation d'une requête API
      toast({
        title: "Paramètres généraux sauvegardés",
        description: "Vos préférences ont été mises à jour avec succès."
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder vos paramètres généraux.",
        variant: "destructive"
      });
    } finally {
      setSavingGeneral(false);
    }
  };

  const savePrivacySettings = async () => {
    setSavingPrivacy(true);
    try {
      // Logique pour sauvegarder les paramètres de confidentialité
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulation d'une requête API
      toast({
        title: "Paramètres de confidentialité sauvegardés",
        description: "Vos préférences de confidentialité ont été mises à jour avec succès."
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder vos paramètres de confidentialité.",
        variant: "destructive"
      });
    } finally {
      setSavingPrivacy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Paramètres de la plateforme</CardTitle>
        <CardDescription>
          Personnalisez votre expérience et gérez vos préférences.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="general">Général</TabsTrigger>
            <TabsTrigger value="privacy">Confidentialité</TabsTrigger>
            <TabsTrigger value="subscription">Abonnement</TabsTrigger>
          </TabsList>
          
          <TabsContent value="general" className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email-notifications">Notifications par email</Label>
                  <p className="text-sm text-muted-foreground">Recevoir des emails pour les activités importantes</p>
                </div>
                <Switch
                  id="email-notifications"
                  checked={generalSettings.emailNotifications}
                  onCheckedChange={() => handleToggleGeneralSetting('emailNotifications')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="marketing-emails">Emails marketing</Label>
                  <p className="text-sm text-muted-foreground">Recevoir des emails sur les nouvelles fonctionnalités</p>
                </div>
                <Switch
                  id="marketing-emails"
                  checked={generalSettings.marketingEmails}
                  onCheckedChange={() => handleToggleGeneralSetting('marketingEmails')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="dark-mode">Mode sombre</Label>
                  <p className="text-sm text-muted-foreground">Utiliser le thème sombre pour l'interface</p>
                </div>
                <Switch
                  id="dark-mode"
                  checked={generalSettings.darkMode}
                  onCheckedChange={() => handleToggleGeneralSetting('darkMode')}
                />
              </div>
              
              <div className="pt-4">
                <Button 
                  onClick={saveGeneralSettings} 
                  disabled={savingGeneral}
                  className="flex items-center gap-2"
                >
                  {savingGeneral ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Sauvegarder les paramètres
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="privacy" className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="public-profile">Profil public</Label>
                  <p className="text-sm text-muted-foreground">Rendre votre profil visible publiquement</p>
                </div>
                <Switch
                  id="public-profile"
                  checked={privacySettings.publicProfile}
                  onCheckedChange={() => handleTogglePrivacySetting('publicProfile')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="share-data">Partage de données</Label>
                  <p className="text-sm text-muted-foreground">Partager des données anonymisées pour améliorer le service</p>
                </div>
                <Switch
                  id="share-data"
                  checked={privacySettings.shareData}
                  onCheckedChange={() => handleTogglePrivacySetting('shareData')}
                />
              </div>
              
              <div className="pt-4">
                <Button 
                  onClick={savePrivacySettings} 
                  disabled={savingPrivacy}
                  className="flex items-center gap-2"
                >
                  {savingPrivacy ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Sauvegarder les paramètres
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="subscription" className="space-y-6">
            <Alert>
              <AlertTitle>Fonctionnalité à venir</AlertTitle>
              <AlertDescription>
                La gestion des abonnements sera bientôt disponible. Vous pourrez choisir parmi différents plans pour accéder à des fonctionnalités supplémentaires.
              </AlertDescription>
            </Alert>
            
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="p-6">
                <CardTitle className="mb-2">Plan Gratuit</CardTitle>
                <CardDescription className="mb-4">Accès aux fonctionnalités de base</CardDescription>
                <ul className="space-y-2 text-sm mb-6">
                  <li>• Profil candidat de base</li>
                  <li>• 3 demandes de feedback par mois</li>
                  <li>• Stockage limité</li>
                </ul>
                <Button variant="outline" className="w-full" disabled>Plan actuel</Button>
              </Card>
              
              <Card className="p-6 border-2 border-blue-200">
                <CardTitle className="mb-2">Plan Premium</CardTitle>
                <CardDescription className="mb-4">Fonctionnalités avancées et accès illimité</CardDescription>
                <ul className="space-y-2 text-sm mb-6">
                  <li>• Tous les profils disponibles</li>
                  <li>• Demandes de feedback illimitées</li>
                  <li>• Fonctionnalités avancées</li>
                  <li>• Support prioritaire</li>
                </ul>
                <Button className="w-full" disabled>Bientôt disponible</Button>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SettingsTab;
